import React from "react";
import moment from "moment";

/**
 * What a leave is worth, shared by the admin listing, the developer's own
 * listing and the dashboard tile, so the screens cannot disagree about which
 * colour "Cancel" is, what counts as a Half day, or how long a leave was.
 *
 * The API is not consistent about the word: `/user-api/my-leave` returns
 * "Cancel" where the admin endpoint returns "Cancelled", and casing varies.
 * `statusKey` strips everything but letters, and every spelling seen in the
 * wild is mapped rather than guessed at - an unknown status simply gets no
 * tone, which is the right failure.
 */
export const statusKey = (status) =>
  String(status ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

export const STATUS_TONE = {
  approved: "success",
  approve: "success",
  pending: "attention",
  hold: "attention",
  onhold: "attention",
  disapproved: "danger",
  unsanctioned: "danger",
  rejected: "danger",
  reject: "danger",
  declined: "danger",
  cancel: "muted",
  cancelled: "muted",
  canceled: "muted",
};

export const leaveTone = (leave) => STATUS_TONE[statusKey(leave?.status)] ?? null;

/* `/user-api/my-leave` says "Cancel" and "Hold" where the tab says
   "Cancelled" and "On hold"; each tab lists every statusKey it covers.
   Shared by the employee's listing and the dashboard's leave dialog. */
export const LEAVE_STATUS_TABS = [
  { value: "all", label: "All", keys: null },
  { value: "pending", label: "Pending", keys: ["pending"], tone: "attention" },
  { value: "approved", label: "Approved", keys: ["approved"] },
  { value: "hold", label: "On hold", keys: ["hold", "onhold"] },
  { value: "disapproved", label: "Disapproved", keys: ["disapproved"] },
  { value: "unsanctioned", label: "Unsanctioned", keys: ["unsanctioned"] },
  { value: "cancelled", label: "Cancelled", keys: ["cancel", "cancelled", "canceled"] },
];

export const leaveStatusTab = (leave) =>
  LEAVE_STATUS_TABS.find((tab) => tab.keys && tab.keys.includes(statusKey(leave?.status)));

/* The four washes, in the order a request moves through them. */
const LEAVE_STATES = [
  { state: "Pending", tone: "attention" },
  { state: "Approved", tone: "success" },
  { state: "Disapproved", tone: "danger" },
  { state: "Cancelled", tone: "muted" },
];

/** Names the row washes. Same component on both leave listings. */
export const LeaveToneLegend = () => (
  <div className="dt-legend">
    {LEAVE_STATES.map((entry) => (
      <span className="dt-legend-item" key={entry.state}>
        <span
          className={"dt-key dt-key-" + entry.tone}
          aria-hidden="true"
        ></span>
        {entry.state}
      </span>
    ))}
  </div>
);

/* --- what kind of leave, and how long --------------------------------------
   `/user-api/my-leave` composes the type as
   "Half day Leave (Sep 17,2026)(Thursday)" while the admin endpoint returns a
   bare "Half day", and the leave form posts three type ids: 1 Full day,
   2 Half day, 3 Short Leave. Every spelling is reduced to one of those three
   kinds - a comparison against the literal string read 0 for five real
   leaves, which is what kept a day figure off the dashboard. */
export const LEAVE_KINDS = ["Full day", "Half day", "Short leave"];

export const leaveKind = (type) => {
  const text = String(type ?? "");
  if (/full\s*day/i.test(text)) return "Full day";
  if (/half\s*day/i.test(text)) return "Half day";
  if (/short/i.test(text)) return "Short leave";
  return text.split("(")[0].trim() || "-";
};

/* A row carries a unix `from_timestamp` / `to_timestamp`, which are
   unambiguous, and an "MM/DD/YY" `from` / `to` text as the fallback. */
const leaveMoment = (stamp, text) => {
  const seconds = Number(stamp);
  return seconds > 0 ? moment.unix(seconds) : moment(text, "MM/DD/YY");
};

export const leaveStart = (leave) =>
  leaveMoment(leave?.from_timestamp, leave?.from);

/**
 * The short leave already booked in the month of `date`, if any.
 *
 * One short leave is allowed per calendar month. The month is the one the
 * leave falls in, not the one it was applied in - a November short leave
 * applied for in October uses up November. A request that was cancelled or
 * disapproved never happened, so it does not use up the month; anything
 * else (pending, approved, on hold, unsanctioned) does.
 */
export const shortLeaveInMonth = (leaves, date) => {
  const month = moment(date);
  return (leaves ?? []).find((leave) => {
    if (leaveKind(leave?.type) !== "Short leave") return false;
    const status = leaveStatusTab(leave)?.value;
    if (status === "cancelled" || status === "disapproved") return false;
    const start = leaveStart(leave);
    return start.isValid() && start.isSame(month, "month");
  });
};

/**
 * How many days a leave costs.
 *
 * Calendar days as the REQUEST records them: a Full day leave spans from its
 * first date to its last inclusive, which is the span the sandwich rule has
 * already been applied to, and a Half day is half of one. A Short leave is
 * measured in hours, not days, so it costs 0 here and is counted by request
 * instead - a leave screen that wants its hours has `time_from`/`time_to`.
 */
export const leaveDays = (leave) => {
  const kind = leaveKind(leave?.type);
  if (kind === "Short leave") return 0;
  if (kind === "Half day") return 0.5;

  const start = leaveStart(leave);
  if (!start.isValid()) return 0;
  const end = leaveMoment(leave?.to_timestamp, leave?.to);
  if (!end.isValid() || !end.isAfter(start, "day")) return 1;
  return end.startOf("day").diff(start.clone().startOf("day"), "days") + 1;
};

/* --- how a leave reads in a table -----------------------------------------
   Shared by the admin listing and the employee's own, so both say "Full day
   · 3 days" over "9:00 AM - 6:45 PM" and "Oct 27 - Oct 29, 2026" the same
   way. The office hours are the ones the admin listing has always shown. */
const clock = (time) => moment(time, "HH:mm:ss").format("h:mm A");

export const leaveTimeFrame = (leave) => {
  switch (leaveKind(leave?.type)) {
    case "Full day":
      return "9:00 AM - 6:45 PM";
    case "Half day":
      return /first/i.test(leave?.duration ?? "") ? "9:00 AM - 2:00 PM" : "2:45 PM - 6:45 PM";
    case "Short leave":
      return leave?.time_from && leave?.time_to
        ? `${clock(leave.time_from)} - ${clock(leave.time_to)}`
        : "";
    default:
      return "";
  }
};

export const leaveDateRange = (leave) => {
  const start = leaveStart(leave);
  if (!start.isValid()) return "";
  const end = leaveMoment(leave?.to_timestamp, leave?.to);
  if (!end.isValid() || !end.isAfter(start, "day")) return start.format("ddd, MMM D, YYYY");
  return start.isSame(end, "year")
    ? `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`
    : `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

/** "Full day · 3 days" over its time frame: the Leave cell on both listings. */
export const LeaveCell = ({ leave }) => {
  const frame = leaveTimeFrame(leave);
  return (
    <div>
      <div className="text-nowrap">
        {leaveKind(leave?.type)}
        {leave?.duration ? <span className="text-muted"> · {leave.duration}</span> : null}
      </div>
      {frame ? (
        <div className="text-muted text-nowrap" style={{ fontSize: "12px" }}>
          {frame}
        </div>
      ) : null}
    </div>
  );
};
